import * as React from 'react';
import { AttachmentType, getLiteAllAttachmentTypesGrouped, getLiteNxtSmAttachmentField } from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { RoofType } from 'projectDesign/components/projectConfiguration/utils/constants';
import { s5AttachmentPreferenceDict, isGeneric, S5_DEFAULT_ULTIMATE_LOAD_UPLIFT, S5_DEFAULT_ULTIMATE_LOAD_SHEAR } from 'projectDesign/components/projectConfiguration/utils/s5AttachmentsFilters';
import { SET_PROJECT_OPTION } from 'projectDesign/components/projectConfiguration/projectConfigurationActions';
import { railClampOption } from 'projectDesign/components/projectConfiguration/fields/railClampOption';
import roofMaterialThicknessSolarMountFlush, {
  rpanelRoofMaterialThicknessForPM9000s,
  rpanelRoofMaterialThicknessForPMAdj,
} from 'projectDesign/components/projectConfiguration/fields/roofMaterialThicknessSolarMountFlush';
import smStandardSeamMetalAttachment from 'projectDesign/components/projectConfiguration/fields/smStandardSeamMetalAttachment';
import smSolarhooksAttachments from 'projectDesign/components/projectConfiguration/fields/smSolarhooksAttachments';
import standoffHeightField from 'projectDesign/components/projectConfiguration/fields/standoffHeight';
import { TileAttachmentType } from 'projectDesign/components/projectConfiguration/fields/smTileReplacementsOrSolarhooks';
import {
  u_anchor_roofs_list,
  extractUniqueMfValues,
  extractUniqueColourValues,
  getDataByKey,
} from 'projectDesign/components/projectConfiguration/utils/uAnchorAttachmentsFilters';
import getManufacturerType from 'projectDesign/components/projectConfiguration/fields/manufacturerField';
import getColourType from 'projectDesign/components/projectConfiguration/fields/colourField';
import {
  rm10AttachmentType,
  rm10AttachmentTypeWithoutUanchor,
} from 'projectDesign/components/projectConfiguration/fields/attachmentType';
import { getAttachmentOptimizationCriteriaField } from 'projectDesign/components/projectConfiguration/fields/attachmentOptimizationCriteria';
import { isNxtHorizon, isSolarMount, isCommercialProduct, isSMAscenderFlush } from '__common/constants/products';

// Maps an attachment_type integer back to the roof-type family it belongs
// to. Used by the UB Lite grouped attachment picker so the conditional
// inputs (and dropdown defaults) follow the picked attachment rather than
// the project's initial roof_type.
export function roofFamilyFromAttachment(
  attachmentTypeInt: number | string | undefined | null,
): number | undefined {
  if (attachmentTypeInt === undefined || attachmentTypeInt === null) return undefined;
  if (attachmentTypeInt === 'standing_seam_s5') return RoofType.STANDING_SEAM;
  const v = Number(attachmentTypeInt);
  if (v === AttachmentType.PM_ADJUST || v === AttachmentType.PM_9000S) return RoofType.R_PANEL;
  if (v === AttachmentType.MINI_1SS || v === AttachmentType.STAND_2SS || v === AttachmentType.WIDE_2SS) return RoofType.STANDING_SEAM;
  if (
    v === AttachmentType.SOLARHOOK_FLAT_TILE ||
    v === AttachmentType.SOLARHOOK_9_IN_BASE ||
    v === AttachmentType.SOLARHOOK_7_IN_BASE ||
    v === AttachmentType.SOLARHOOK_UNIVERSAL ||
    v === AttachmentType.PERFORMANCE_HOOK
  ) return RoofType.TILE;
  if (
    v === AttachmentType.L_FOOT_ONLY ||
    v === AttachmentType.FLASHKIT_PRO ||
    v === AttachmentType.FLASHLOC_COMP_KIT ||
    v === AttachmentType.FLASHLOC_DUO ||
    v === AttachmentType.FLASHKIT_PRO_SB ||
    v === AttachmentType.SM_BUTYL_DTD ||
    v === AttachmentType.STRONGHOLD_ATT_KIT_COMP ||
    v === AttachmentType.STRONGHOLD_ATT_BUTYL
  ) return RoofType.SHINGLE;
  return undefined;
}

// Callback signature — each host page maps a single field change to its own
// persistence flow. BomPanel dispatches SET_PROJECT_OPTION + an immediate
// UPDATE_LITE_PROJECT_REQUEST(regenBom); CompleteBomPage dispatches
// SET_PROJECT_OPTION + persistFieldChange (configOverride PUT).
export type OnAttachmentFieldChange = (apiField: string, value: number | string | null) => void;

export type ConditionalAttachmentFieldsInputs = {
  roofType: number | undefined | null;
  productId: number | undefined | null;
  attachmentTypeInt: number | undefined;
  s5OrSci: string | undefined;
  tileReplacementOrSolarhooks: number | undefined;
  railClampOption: number | undefined;
  materialThickness: number | undefined;
  roofSubstrate: number | undefined;
  standoffHeight: number | undefined;
  manufacturer?: string;
  colour?: string;
  isUbuilderLite: boolean;
  allowMechanicalAttachments?: number;
  attachmentOptimizationCriteria?: number;
  upliftAllowable?: number;
  shearAllowable?: number;
  onFieldChange: OnAttachmentFieldChange;
};

// Renders a single select field row below the attachment dropdown.
export function renderAttachmentFieldRow(
  label: string,
  apiField: string,
  options: Array<{ value: number | string; name: string } | any>,
  currentValue: number | string | undefined | null,
  onFieldChange: OnAttachmentFieldChange,
) {
  const currentStr = currentValue !== undefined && currentValue !== null ? String(currentValue) : '';
  const isStringValued = typeof options[0]?.value === 'string';
  return (
    <div className="v3-bom-att-field" key={apiField}>
      <label className="v3-bom-att-field-lbl">{label}</label>
      <select
        className="v3-bom-finish-sel"
        value={currentStr}
        onChange={(e) => {
          const raw = e.target.value;
          const next: number | string = isStringValued ? raw : Number(raw);
          onFieldChange(apiField, next);
        }}>
        <option value="">— Select —</option>
        {options.map((opt: any) => (
          <option key={String(opt.value)} value={String(opt.value)}>{opt.name}</option>
        ))}
      </select>
    </div>
  );
}

// Renders a numeric input field row (for uplift/shear allowable).
export function renderAttachmentNumericRow(
  label: string,
  apiField: string,
  currentValue: number | undefined | null,
  onFieldChange: OnAttachmentFieldChange,
) {
  return (
    <div className="v3-bom-att-field" key={apiField}>
      <label className="v3-bom-att-field-lbl">{label}</label>
      <input
        className="v3-bom-finish-sel"
        type="number"
        min={1}
        placeholder="—"
        value={currentValue !== undefined && currentValue !== null ? currentValue : ''}
        onChange={(e) => {
          const val = e.target.value === '' ? null : Number(e.target.value);
          onFieldChange(apiField, val);
        }}
      />
    </div>
  );
}

// Renders the roof-type-conditional fields below the main attachment
// dropdown. Mirrors the conditional logic from NxtHorizonConfig.ts —
// which fields appear depends on roof_type and the currently-selected
// attachment/preference values.
//
// Shared between BomPanel (right-panel BOM) and CompleteBomPage (full BOM
// page). Each host wires up its own onFieldChange so the same UI can drive
// two different persistence flows (see OnAttachmentFieldChange).
export function renderConditionalAttachmentFields(props: ConditionalAttachmentFieldsInputs): React.ReactNode {
  const {
    roofType,
    productId,
    attachmentTypeInt,
    s5OrSci,
    tileReplacementOrSolarhooks,
    railClampOption: railClampVal,
    materialThickness,
    roofSubstrate,
    standoffHeight: standoffVal,
    manufacturer,
    colour,
    isUbuilderLite,
    allowMechanicalAttachments,
    attachmentOptimizationCriteria,
    upliftAllowable,
    shearAllowable,
    onFieldChange,
  } = props;

  const isCommercialRM = isCommercialProduct(productId);
  if (!productId || (!isNxtHorizon(productId) && !isSolarMount(productId) && !isCommercialRM)) return null;

  // UB Lite NXT/SM: derive the effective roof family from the picked
  // attachment_type so the conditional inputs follow the dropdown
  // selection rather than the project's initial roof_type.
  const liteNxtSm = !!isUbuilderLite && (isNxtHorizon(productId) || isSolarMount(productId));
  const effectiveRoofType: number | string | undefined | null = liteNxtSm
    ? (tileReplacementOrSolarhooks === TileAttachmentType.TILE_REPLACEMENT
      ? RoofType.TILE
      : roofFamilyFromAttachment(attachmentTypeInt)) ?? roofType
    : roofType;
  const effectiveS5OrSci = liteNxtSm
    ? (roofFamilyFromAttachment(attachmentTypeInt) === RoofType.STANDING_SEAM
      ? s5AttachmentPreferenceDict.sci
      : s5OrSci)
    : s5OrSci;
  const effectiveTileReplacementOrSolarhooks = liteNxtSm && roofFamilyFromAttachment(attachmentTypeInt) === RoofType.TILE
    ? TileAttachmentType.SOLARHOOKS
    : tileReplacementOrSolarhooks;
  if (effectiveRoofType === undefined || effectiveRoofType === null) return null;

  const fields: React.ReactNode[] = [];

  // ── SHINGLE: Attachment Fastening On (roof_substrate) when Butyl or FLASHLOC DUO
  if (effectiveRoofType === RoofType.SHINGLE) {
    const needsSubstrate = [
      AttachmentType.STRONGHOLD_ATT_BUTYL,
      AttachmentType.FLASHLOC_DUO,
      AttachmentType.SM_BUTYL_DTD,
    ].includes(attachmentTypeInt as number);
    if (needsSubstrate) {
      fields.push(renderAttachmentFieldRow('Attachment Fastening On', 'roof_substrate', [
        { value: 3, name: 'Rafters' },
        { value: 1, name: 'Direct to Deck (OSB)' },
        { value: 2, name: 'Direct to Deck (Plywood)' },
      ], roofSubstrate, onFieldChange));
    }
  }

  // ── STANDING SEAM METAL
  if (effectiveRoofType === RoofType.STANDING_SEAM) {
    // Rail Clamp Option — NXT only (SM never shows it)
    if (isNxtHorizon(productId)) {
      fields.push(renderAttachmentFieldRow('Rail Clamp Option', 'rail_clamp_option', railClampOption.data, railClampVal, onFieldChange));
    }

    if (effectiveS5OrSci === s5AttachmentPreferenceDict.sci) {
      // SCI: Material Thickness + SSM Attachments. In UB Lite the SSM
      // attachment_type picker is already part of the grouped main
      // dropdown, so only the material-thickness input is added here.
      fields.push(renderAttachmentFieldRow('Roof Material Thickness (GA)', 'material_thickness', roofMaterialThicknessSolarMountFlush.data, materialThickness, onFieldChange));
      if (!liteNxtSm) {
        fields.push(renderAttachmentFieldRow('Standing Seam Metal Attachments', 'attachment_type', smStandardSeamMetalAttachment.data, attachmentTypeInt, onFieldChange));
      }
    }
  }

  // ── R-PANEL
  if (effectiveRoofType === RoofType.R_PANEL) {
    // Rail Clamp Option — NXT only (SM never shows it)
    if (isNxtHorizon(productId)) {
      fields.push(renderAttachmentFieldRow('Rail Clamp Option', 'rail_clamp_option', railClampOption.data, railClampVal, onFieldChange));
    }
    // Material thickness options depend on attachment type
    const thicknessField = attachmentTypeInt === AttachmentType.PM_9000S
      ? rpanelRoofMaterialThicknessForPM9000s
      : rpanelRoofMaterialThicknessForPMAdj;
    fields.push(renderAttachmentFieldRow('Roof Material Thickness (GA)', 'material_thickness', thicknessField.data, materialThickness, onFieldChange));
  }

  // ── TILE
  if (effectiveRoofType === RoofType.TILE) {
    // In UB Lite the Solarhooks attachment_type picker is already part of
    // the grouped main dropdown, so it's omitted from the conditional rows.
    if (effectiveTileReplacementOrSolarhooks === TileAttachmentType.SOLARHOOKS && !liteNxtSm) {
      fields.push(renderAttachmentFieldRow('Solarhooks Attachments', 'attachment_type', smSolarhooksAttachments.data, attachmentTypeInt, onFieldChange));
    }
    if (effectiveTileReplacementOrSolarhooks === TileAttachmentType.STANDOFFS) {
      fields.push(renderAttachmentFieldRow('Standoff Height', 'standoff_height', standoffHeightField.data, standoffVal, onFieldChange));
    }
  }

  // ── COMMERCIAL (RM10/GF5/GF10) — Roof Manufacturer + Colour + Attachment Inputs
  if (isCommercialRM) {
    const rt = roofType !== undefined && roofType !== null ? Number(roofType) : 0;

    // Roof Manufacturer + Colour — only for U-Anchor roofs (6-8, 17-22).
    // Hidden in UB Lite for RM products; defaults are seeded in
    // getCommercialMandatoryDefaults.
    if (!isUbuilderLite && u_anchor_roofs_list.includes(rt)) {
      const uAnchorData = getDataByKey(rt);
      if (uAnchorData && uAnchorData.length > 0) {
        const mfValues = extractUniqueMfValues(uAnchorData);
        const mfField = getManufacturerType(mfValues);
        fields.push(renderAttachmentFieldRow('Roof Manufacturer', 'manufacturer', mfField.data, manufacturer, onFieldChange));

        if (manufacturer) {
          const colourValues = extractUniqueColourValues(uAnchorData);
          const colourField = getColourType(colourValues);
          const hideColour = colourField.data.length === 1 && colourField.data[0].value === 'N';
          if (!hideColour) {
            fields.push(renderAttachmentFieldRow('Color', 'colour', colourField.data, colour, onFieldChange));
          }
        }
      }
    }

    // Attachment Inputs — all roof types. Hidden in UB Lite for RM
    // products; defaults are seeded in buildLiteUpdatePayload.
    if (!isUbuilderLite) {
      fields.push(renderAttachmentFieldRow('Allow Mechanical Attachments', 'allow_mechanical_attachments',
        [{ value: 1, name: 'YES' }, { value: 0, name: 'NO' }], allowMechanicalAttachments, onFieldChange));

      if (allowMechanicalAttachments === 1) {
        // Optimize Attachments For
        const optimizeField = getAttachmentOptimizationCriteriaField(productId);
        fields.push(renderAttachmentFieldRow('Optimize Attachments For', 'attachment_optimization_criteria',
          optimizeField.data, attachmentOptimizationCriteria, onFieldChange));

        // Attachment Type — exclude U-Anchor if roof not in U-Anchor list
        const attTypeField = u_anchor_roofs_list.includes(rt) ? rm10AttachmentType : rm10AttachmentTypeWithoutUanchor;
        fields.push(renderAttachmentFieldRow('Attachment Type', 'attachment_type', attTypeField.data, attachmentTypeInt, onFieldChange));

        // Uplift + Shear Allowable — shown for all attachment types
        if (attachmentTypeInt) {
          fields.push(renderAttachmentNumericRow('Uplift Allowable (lbs)', 'uplift_allowable', upliftAllowable, onFieldChange));
          fields.push(renderAttachmentNumericRow('Shear Allowable (lbs)', 'shear_allowable', shearAllowable, onFieldChange));
        }
      }
    }
  }

  if (fields.length === 0) return null;
  return <div className="v3-bom-att-fields">{fields}</div>;
}

// Inputs required to resolve the currently-selected attachment option and
// the field descriptor that drives the dropdown. Shared between BomPanel
// (workspace right panel) and CompleteBomPage (full BOM page).
export type ResolveLiteAttachmentFieldInputs = {
  productId: number | undefined | null;
  roofType: number | undefined | null;
  railsProductId: number | undefined | null;
  attachmentTypeInt: number | undefined;
  s5OrSci: string | undefined;
  tileReplacementOrSolarhooks: number | undefined;
  isUbuilderLite: boolean;
  tilt?: number | null;
};

// Resolves the field descriptor + currently-selected value for the
// attachment dropdown. In UB Lite mode returns the grouped ALL-attachments
// picker; falls back to legacy per-(product,roof) field selection.
export function resolveLiteAttachmentField(
  inputs: ResolveLiteAttachmentFieldInputs,
): { field: any; currentValue: number | string | undefined } | null {
  const {
    productId,
    roofType,
    railsProductId,
    attachmentTypeInt,
    s5OrSci,
    tileReplacementOrSolarhooks,
    isUbuilderLite,
    tilt,
  } = inputs;
  if (productId === undefined || productId === null) return null;
  if (!isNxtHorizon(productId) && !isSolarMount(productId)) return null;

  if (isUbuilderLite) {
    const grouped: any = getLiteAllAttachmentTypesGrouped(productId, roofType ?? undefined, railsProductId);
    if (grouped) {
      let currentValue: any = (roofType === RoofType.TILE && tileReplacementOrSolarhooks === TileAttachmentType.TILE_REPLACEMENT)
        ? 'tile_replacement'
        : attachmentTypeInt;

      const isSmAscenderShingle = isSolarMount(productId) && isSMAscenderFlush(railsProductId) && roofType === RoofType.SHINGLE;

      const currentFamily = currentValue === 'tile_replacement' ? RoofType.TILE : roofFamilyFromAttachment(currentValue);
      if (currentValue !== undefined && currentValue !== null && currentFamily !== undefined && currentFamily !== roofType) {
        currentValue = null;
      }

      if (currentValue === undefined || currentValue === null) {
        if (roofType === RoofType.TILE) {
          currentValue = 'tile_replacement';
        } else if (roofType === RoofType.R_PANEL) {
          currentValue = AttachmentType.PM_9000S;
        } else if (roofType === RoofType.STANDING_SEAM) {
          currentValue = AttachmentType.MINI_1SS;
        } else if (roofType === RoofType.SHINGLE) {
          currentValue = isSmAscenderShingle
            ? AttachmentType.L_FOOT_ONLY
            : isNxtHorizon(productId)
            ? AttachmentType.STRONGHOLD_ATT_BUTYL
            : (isSolarMount(productId) && tilt === null)
              ? AttachmentType.SM_BUTYL_DTD
              : AttachmentType.FLASHKIT_PRO;
        } else if (roofType === RoofType.OTHERS) {
          currentValue = isNxtHorizon(productId)
            ? AttachmentType.STRONGHOLD_ATT_KIT_COMP
            : AttachmentType.FLASHKIT_PRO;
        }
      }

      return { field: grouped, currentValue };
    }
  }

  if (roofType === undefined || roofType === null) return null;
  const field: any = getLiteNxtSmAttachmentField(productId, roofType, railsProductId);
  if (!field) return null;
  const currentValue =
    field.apiField === 's5_or_sci' ? s5OrSci :
      field.apiField === 'tile_replacement_or_solarhooks' ? tileReplacementOrSolarhooks :
        attachmentTypeInt;
  return { field, currentValue };
}

export type RenderLiteAttachmentSelectProps = ResolveLiteAttachmentFieldInputs & {
  dispatch: Function;
  selectClassName: string;
  // Called once per user change with the full multi-field patch (roof_type,
  // attachment_type, s5_or_sci, tile_replacement_or_solarhooks, and S-5
  // defaults where applicable). Host maps this to its persistence flow —
  // BomPanel dispatches UPDATE_LITE_PROJECT_REQUEST({regenBom:true});
  // CompleteBomPage calls persistFieldChange(patch).
  onPersist: (patch: { [key: string]: any }) => void;
};

// Renders the UB Lite attachment-type dropdown (grouped by roof family
// when getLiteAllAttachmentTypesGrouped returns a grouped field, else the
// legacy per-(product,roof) select). Dispatches SET_PROJECT_OPTION for
// every affected field and forwards the patch to `onPersist` so the host
// can trigger its own regen/persist call.
export function renderLiteAttachmentSelect(props: RenderLiteAttachmentSelectProps): React.ReactNode | null {
  const resolved = resolveLiteAttachmentField(props);
  if (!resolved) return null;
  const { field, currentValue } = resolved;
  const { dispatch, selectClassName, onPersist } = props;
  const groups: Array<{ label: string; data: Array<{ value: number | string; name: string }> }> | undefined = field.groups;
  const flatOptions: Array<{ value: number | string; name: string }> =
    field.data || (groups ? groups.reduce((acc: any[], g: any) => acc.concat(g.data), [] as any[]) : []);
  const isStringValued = field.apiField === 's5_or_sci';
  const currentStr = currentValue !== undefined && currentValue !== null ? String(currentValue) : '';
  const inList = currentValue !== undefined && flatOptions.some(o => String(o.value) === currentStr);

  const handleChange = (e: any) => {
    const raw = e.target.value;
    if (!dispatch) return;

    if (groups) {
      const nextVal = Number(raw);
      const roofFamily = raw === 'tile_replacement'
        ? RoofType.TILE
        : (raw === 'standing_seam_s5' ? RoofType.STANDING_SEAM : roofFamilyFromAttachment(nextVal));
      const patch: { [key: string]: any } = {};
      if (roofFamily !== undefined && roofFamily !== props.roofType) {
        dispatch(SET_PROJECT_OPTION('roof_type', roofFamily));
        patch.roof_type = roofFamily;
      }
      if (raw === 'standing_seam_s5') {
        dispatch(SET_PROJECT_OPTION('s5_or_sci', s5AttachmentPreferenceDict.s_5));
        dispatch(SET_PROJECT_OPTION('attachment_type', null));
        dispatch(SET_PROJECT_OPTION('s5_roof_manufacturer', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_panel', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_material', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_clamp', 'S-5-N Mini'));
        dispatch(SET_PROJECT_OPTION('s5_ultimate_load_uplift', S5_DEFAULT_ULTIMATE_LOAD_UPLIFT));
        dispatch(SET_PROJECT_OPTION('s5_ultimate_load_shear', S5_DEFAULT_ULTIMATE_LOAD_SHEAR));
        Object.assign(patch, {
          s5_or_sci: s5AttachmentPreferenceDict.s_5,
          attachment_type: null,
          s5_roof_manufacturer: isGeneric,
          s5_roof_panel: isGeneric,
          s5_roof_material: isGeneric,
          s5_clamp: 'S-5-N Mini',
          s5_ultimate_load_uplift: S5_DEFAULT_ULTIMATE_LOAD_UPLIFT,
          s5_ultimate_load_shear: S5_DEFAULT_ULTIMATE_LOAD_SHEAR,
        });
      } else if (raw === 'tile_replacement') {
        dispatch(SET_PROJECT_OPTION('tile_replacement_or_solarhooks', TileAttachmentType.TILE_REPLACEMENT));
        dispatch(SET_PROJECT_OPTION('attachment_type', null));
        dispatch(SET_PROJECT_OPTION('s5_or_sci', null));
        Object.assign(patch, {
          tile_replacement_or_solarhooks: TileAttachmentType.TILE_REPLACEMENT,
          attachment_type: null,
          s5_or_sci: null,
        });
      } else {
        const sciValues = [
          AttachmentType.MINI_1SS,
          AttachmentType.STAND_2SS,
          AttachmentType.WIDE_2SS,
        ] as any[];
        if (sciValues.includes(nextVal)) {
          dispatch(SET_PROJECT_OPTION('s5_or_sci', s5AttachmentPreferenceDict.sci));
          dispatch(SET_PROJECT_OPTION('tile_replacement_or_solarhooks', null));
          dispatch(SET_PROJECT_OPTION('attachment_type', nextVal));
          Object.assign(patch, {
            s5_or_sci: s5AttachmentPreferenceDict.sci,
            tile_replacement_or_solarhooks: null,
            attachment_type: nextVal,
          });
        } else {
          dispatch(SET_PROJECT_OPTION('s5_or_sci', null));
          const solarhookValues = [
            AttachmentType.SOLARHOOK_FLAT_TILE,
            AttachmentType.SOLARHOOK_9_IN_BASE,
            AttachmentType.SOLARHOOK_7_IN_BASE,
            AttachmentType.SOLARHOOK_UNIVERSAL,
            AttachmentType.PERFORMANCE_HOOK,
          ] as any[];
          if (solarhookValues.includes(nextVal)) {
            dispatch(SET_PROJECT_OPTION('tile_replacement_or_solarhooks', TileAttachmentType.SOLARHOOKS));
            dispatch(SET_PROJECT_OPTION('attachment_type', nextVal));
            Object.assign(patch, {
              s5_or_sci: null,
              tile_replacement_or_solarhooks: TileAttachmentType.SOLARHOOKS,
              attachment_type: nextVal,
            });
          } else {
            dispatch(SET_PROJECT_OPTION('tile_replacement_or_solarhooks', null));
            dispatch(SET_PROJECT_OPTION('attachment_type', nextVal));
            Object.assign(patch, {
              s5_or_sci: null,
              tile_replacement_or_solarhooks: null,
              attachment_type: nextVal,
            });
          }
        }
      }
      onPersist(patch);
      return;
    }

    const next: number | string = isStringValued ? raw : Number(raw);
    if (isStringValued || !isNaN(next as number)) {
      dispatch(SET_PROJECT_OPTION(field.apiField, next));
      if (field.apiField === 's5_or_sci' && next === s5AttachmentPreferenceDict.s_5) {
        dispatch(SET_PROJECT_OPTION('s5_roof_manufacturer', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_panel', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_roof_material', isGeneric));
        dispatch(SET_PROJECT_OPTION('s5_clamp', 'S-5-N Mini'));
        onPersist({
          [field.apiField]: next,
          s5_roof_manufacturer: isGeneric,
          s5_roof_panel: isGeneric,
          s5_roof_material: isGeneric,
          s5_clamp: 'S-5-N Mini',
        });
      } else {
        onPersist({ [field.apiField]: next });
      }
    }
  };

  return (
    <select
      className={selectClassName}
      value={currentStr}
      onChange={handleChange}>
      {!inList && currentValue !== undefined && currentValue !== null && (
        <option value={currentStr}>{`Current (legacy: ${currentStr})`}</option>
      )}
      {groups
        ? groups.map(g => {
          if (!g.label) {
            return g.data.map(opt => (
              <option key={String(opt.value)} value={String(opt.value)} style={{ fontWeight: 'bold' }}>{opt.name}</option>
            ));
          }
          return (
            <optgroup key={g.label} label={g.label}>
              {g.data.map(opt => (
                <option key={String(opt.value)} value={String(opt.value)}>{opt.name}</option>
              ))}
            </optgroup>
          );
        })
        : flatOptions.map(opt => (
          <option key={String(opt.value)} value={String(opt.value)}>{opt.name}</option>
        ))}
    </select>
  );
}
